import React from 'react';
import { C } from '../../theme.js';
import { Button, Code, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { OutputNameField } from '../shared/OutputNameField.jsx';
import { SourceSelect } from '../shared/SourceSelect.jsx';
import { outputNameIssue, pipelineOrder } from '../../data.js';
import { ColumnsContext, Segmented } from '../StepInspector.jsx';
import { sourceColumns } from '../shared/sourceColumns.js';
import { checkExpression } from './expressionCheck.js';
import { ExpressionEditorModal } from './ExpressionEditorModal.jsx';
import { resolveEditorRows } from './codeMode/rowSource.js';
import {
  blockToggleWrite, builderBlockPatch, codeBlockPatch, firstLines, hasSavedBlockCode,
  isCodeBlock, needsBlockReview,
} from './codeMode/blockCode.js';

// When Source is something other than self, Compute derives its new column
// against a chosen Source's own rows (unset/"__self__" means self, exactly
// as before every existing pipeline was built). Same self-preserving
// contract as Unpivot/Group By/Filter: execute_pipeline (executor.py)
// reverts next_rows back to the unchanged self payload after this step
// when Source isn't self, so the computed result is only reachable through
// Output Name — required in that case, not optional. Unlike
// Arithmetic/Lookup, there's no pre-existing dual-role field to protect
// here, so this doesn't need a self-empty gate: the field is new, and
// unset/self is always a no-op for any pipeline that doesn't set it.
export function ComputeInspector({ step, onChange, pipeline, secondaryDatasets, sheets, onBrowseJoinFile, runMap, uploadedRows }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });

  const sourceIsSelf = !c.source || c.source === '__self__';
  const outputNameIssueRes = outputNameIssue(c.output_name, { sheets, secondaryDatasets, steps: pipeline?.steps, stepId: step.id });
  const outputNameMissing = !sourceIsSelf && !(c.output_name || '').trim();

  // Validate once the user pauses typing, so half-typed formulas aren't flagged.
  const liveCols = React.useContext(ColumnsContext);
  const srcCols = sourceColumns(c.source, { sheets, secondaryDatasets, pipeline, runMap });
  const colNames = ((sourceIsSelf ? liveCols : srcCols) || []).map((x) => x.name);
  const [exprError, setExprError] = React.useState(null);
  React.useEffect(() => {
    const t = setTimeout(() => setExprError(checkExpression(c.expression, colNames)), 700);
    return () => clearTimeout(t);
  }, [c.expression, colNames.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Block-level Code mode (codeMode/blockCode.js — the same shape Case
  // When's block code uses, with exactly one output: `target`). Choosing
  // "Code" with nothing saved stays local (`codeDraft`) and opens the
  // editor; `logic_mode:"code"` and `code` are written together, only by
  // Apply. Closing that first editor without Apply writes nothing and the
  // toggle returns to Builder.
  const [codeDraft, setCodeDraft] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  React.useEffect(() => { setCodeDraft(false); setEditing(false); }, [step.id]);
  const codeView = isCodeBlock(c) || codeDraft;
  const chooseMode = (next) => {
    if (next === 'code') {
      const write = blockToggleWrite(c);
      if (write) u(write);
      else { setCodeDraft(true); setEditing(true); }
    } else {
      setCodeDraft(false);
      if (isCodeBlock(c)) u(builderBlockPatch);
    }
  };
  const editorRows = React.useMemo(() => resolveEditorRows({
    source: c.source, orderedSteps: pipelineOrder(pipeline?.steps || []), stepId: step.id,
    runMap, sheets, secondaryDatasets, uploadedRows, steps: pipeline?.steps,
  }), [c.source, pipeline?.steps, step.id, runMap, sheets, secondaryDatasets, uploadedRows]);
  const editorColumns = (sourceIsSelf ? liveCols : srcCols) || [];

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Segmented
          value={codeView ? 'code' : 'builder'}
          onChange={chooseMode}
          options={[{ value: 'builder', label: 'Builder' }, { value: 'code', label: 'Code' }]}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <FieldLabel hint="Dataset to compute over — an uploaded sheet, an earlier published step, or this block's own data">Source</FieldLabel>
        <SourceSelect
          value={c.source}
          onChange={(v) => u({ source: v })}
          secondaryDatasets={secondaryDatasets}
          sheets={sheets}
          pipeline={pipeline}
          currentStep={step}
          onBrowse={onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined}
          buildTargetName={pipeline?.inputFile?.buildTargetName}
        />
      </div>

      <FieldLabel hint="The computed value is written here">Output column</FieldLabel>
      <TextInput mono value={c.target} onChange={(v) => u({ target: v })} placeholder="e.g. email_domain" />

      {codeView ? (
        <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Eyebrow style={{ marginBottom: 0 }}>Code</Eyebrow>
            <div style={{ flex: 1 }} />
            <Button kind="accent" size="sm" onClick={() => setEditing(true)}>{hasSavedBlockCode(c) ? 'Edit code' : 'Write code'}</Button>
          </div>
          {needsBlockReview(c) && (
            <div style={{ background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', marginBottom: 8 }}>
              Imported code: open the editor and Apply to enable it
            </div>
          )}
          {hasSavedBlockCode(c) ? (() => {
            const fl = firstLines(c.code.src, 8);
            return (
              <pre title="Edit code to change it" style={{
                margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '.76rem', color: C.fg2,
                background: C.app, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px',
                overflowX: 'auto', whiteSpace: 'pre',
              }}>{fl.text}{fl.more ? `\n… ${fl.more} more line${fl.more === 1 ? '' : 's'}` : ''}</pre>
            );
          })() : (
            <div style={{ color: C.fg5, fontSize: '.78rem' }}>No code yet. Write a function body that returns the value for each row.</div>
          )}
          <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 8 }}>
            The Expression setting (hidden while in Code mode) is kept but not used.
          </div>
        </div>
      ) : (<>
        <FieldLabel hint="Reference cells with {column}">Expression</FieldLabel>
        <TextInput mono value={c.expression} onChange={(v) => u({ expression: v })}
          placeholder={'CONCAT({name}," <",{email},">")'}
          style={exprError ? { borderColor: C.error, textDecoration: 'underline wavy', textDecorationColor: C.error } : undefined} />
        {exprError && (
          <div style={{ color: C.error, fontSize: '.72rem', marginTop: 4 }}>
            Check the expression: {exprError}
          </div>
        )}
        <div style={{
          background: C.input, border: `1px solid ${C.border}`, borderRadius: 8,
          padding: 10, marginTop: 8, fontSize: '.72rem', color: C.fg4, lineHeight: 1.7,
        }}>
          <div style={{ color: C.fg2, marginBottom: 4, fontWeight: 600 }}>Functions</div>
          <Code>UPPER(x)</Code> <Code>LOWER(x)</Code> <Code>TITLE(x)</Code> <Code>TRIM(x)</Code><br />
          <Code>CONCAT(a,b,…)</Code> <Code>REPLACE(x,from,to)</Code><br />
          <Code>LEFT(x,n)</Code> <Code>RIGHT(x,n)</Code> <Code>LEN(x)</Code> <Code>BLANK</Code>
        </div>
      </>)}

      <OutputNameField
        value={c.output_name}
        onChange={(v) => u({ output_name: v })}
        required={outputNameMissing}
        hint={sourceIsSelf
          ? 'Optional — also publish this result under a name so later blocks can pick it as their Source'
          : "Required — Source isn't self, so this step's computed result doesn't touch the live pipeline payload; without a name it isn't reachable anywhere"}
        warningMessage={<>Source is &ldquo;{c.source}&rdquo;, not self — without an Output Name this step&rsquo;s result goes nowhere: the live pipeline payload stays unchanged and the computed rows aren&rsquo;t saved anywhere.</>}
        placeholder="e.g., mas_divided"
        issue={outputNameIssueRes}
      />

      {editing && (
        <ExpressionEditorModal
          kind="value_body"
          title="Compute editor"
          outputs={[c.target || 'computed']}
          initialSrc={c.code?.src || ''}
          columns={editorColumns}
          rows={editorRows.rows}
          rowsOrigin={editorRows.origin}
          rowsNote={editorRows.note}
          pipelineId={pipeline?.id}
          onClose={() => { setEditing(false); if (!hasSavedBlockCode(c)) setCodeDraft(false); }}
          onApply={(src) => {
            setCodeDraft(false);
            setEditing(false);
            u(codeBlockPatch(src));
          }}
        />
      )}
    </div>
  );
}
